<template>
    <v-container fluid>
        <v-data-table
            :headers="headers"
            :items="itemsFiltered"
            class="elevation-0"
            :mobile-breakpoint="0"
            :loading="loading"
            disable-pagination
            hide-default-footer
            fixed-header
        >
            <template v-slot:top>
                <v-row
                    no-gutter
                    class="mt-0 mx-0 pt-4 pb-0 pb-4"
                    :style="{ 'background-color': '#eeeeee' }"
                >
                    <v-col cols="12" class="d-flex mb-n3">
                        <h1 class="mr-4">FOREIGN TRADE</h1>
                        <v-btn
                            rounded
                            color="primary"
                            @click="openCreateItem"
                            class="mx-2"
                        >
                            <v-icon>
                                mdi-plus
                            </v-icon>
                            {{ $vuetify.breakpoint.smAndDown ? '' : 'NEW' }}
                        </v-btn>
                    </v-col>
                </v-row>
            </template>
            <template v-slot:[`header.name`]="{ header }">
                <v-text-field
                    class="pb-2 pt-3 mt-2"
                    :label="header.text"
                    v-model="searchByName"
                    dense
                    prepend-icon="mdi-filter"
                />
            </template>
            <template v-slot:[`item.name`]="{ item }">
                <div class="d-flex justify-left px-8">
                    <p class="my-0 text-capitalize">
                        {{ item.name }}
                    </p>
                </div>
            </template>
            <template v-slot:[`item.actions`]="{ item }">
                <div class="d-flex justify-center">
                    <v-btn small icon @click="openEditItem(item)">
                        <v-icon>mdi-pencil</v-icon>
                    </v-btn>
                    <v-btn small icon @click="openDeleteItem(item)">
                        <v-icon>mdi-delete</v-icon>
                    </v-btn>
                </div>
            </template>
        </v-data-table>

        <v-dialog
            v-model="openItemForm"
            :retain-focus="false"
            persistent
            width="600px"
        >
            <v-card :loading="formLoading" v-if="openItemForm">
                <v-card-title>
                    <v-btn
                        small
                        icon
                        @click="closeEditItem"
                        color="primary"
                        class="mr-2"
                    >
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                    <h3 class="text-capitalize">
                        {{ createForm ? 'Link supplier' : 'Edit supplier' }}
                    </h3>
                </v-card-title>
                <v-divider class="ml-4 mr-5" />
                <v-card-text>
                    <v-autocomplete
                        v-model="selectedSupplierId"
                        :items="availableSuppliers"
                        item-text="name"
                        item-value="id"
                        label="Supplier"
                        prepend-icon="mdi-account-box-outline"
                        :rules="[v => !!v || 'Supplier is required']"
                        outlined
                        dense
                        clearable
                    />
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn
                        color="primary"
                        class="mb-4"
                        @click="saveItem"
                        rounded
                        :loading="formLoading"
                        :disabled="!selectedSupplierId"
                    >
                        SAVE
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog
            v-model="openDeleteWarning"
            persistent
            max-width="400px"
            :retain-focus="false"
        >
            <v-card :loading="loading" v-if="openDeleteWarning">
                <v-card-title class="text-h5">
                    Unlink supplier
                </v-card-title>
                <v-card-text>
                    Are you sure you want to remove this supplier from foreign
                    trade auto-routing?
                </v-card-text>
                <v-card-actions>
                    <v-btn text color="secondary" @click="closeDeleteItem">
                        Close
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn
                        @click="deleteItem"
                        text
                        color="error"
                        :loading="loading"
                    >
                        Delete
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import { mapMutations } from 'vuex'
import API from '@/services/api'
import _ from 'lodash'

export default {
    name: 'InvoiceForeignTrade',
    props: {
        setting: {
            type: Object,
            default: () => ({}),
        },
    },
    data: () => ({
        loading: false,
        formLoading: false,
        items: [],
        suppliers: [],
        selectedItem: {},
        selectedSupplierId: null,
        createForm: true,
        openItemForm: false,
        openDeleteWarning: false,
        headers: [
            {
                text: 'SUPPLIER',
                value: 'name',
                sortable: false,
            },
            {
                text: 'ACTIONS',
                value: 'actions',
                sortable: false,
                align: 'center',
            },
        ],
        searchByName: undefined,
    }),
    async mounted() {
        try {
            this.loading = true
            const [invoiceSettingsResponse, suppliers] = await Promise.all([
                API.getInvoiceSettings(this.setting.id),
                API.getSuppliers(),
            ])
            const {
                data: {
                    invoiceSettings: { foreignTradeSuppliers },
                },
            } = invoiceSettingsResponse
            this.items = foreignTradeSuppliers || []
            this.suppliers = suppliers || []
        } catch (error) {
            this.setErrorItems({
                name: this.$options.name,
                message: error.message,
            })
        } finally {
            this.loading = false
        }
    },
    computed: {
        linkedSupplierIds() {
            return new Set(
                (this.items || [])
                    .filter(
                        item =>
                            !this.selectedItem?.id ||
                            item.id !== this.selectedItem.id
                    )
                    .map(item => item.supplierId)
            )
        },
        availableSuppliers() {
            return (this.suppliers || []).filter(
                supplier => !this.linkedSupplierIds.has(supplier.id)
            )
        },
        itemsFiltered() {
            if (!this.items || this.items.length === 0) {
                return []
            }
            if (!this.searchByName) {
                return this.items
            }
            return this.items.filter(item => this.filterByName(item))
        },
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        filterByName(item) {
            return (
                item.name &&
                item.name
                    .toLowerCase()
                    .includes(this.searchByName.toLowerCase())
            )
        },
        replaceItem(itemToUpdate) {
            const index = this.items.findIndex(
                item => item.id == itemToUpdate.id
            )
            if (index >= 0) {
                this.items.splice(index, 1, {
                    ...this.items[index],
                    ...itemToUpdate,
                })
            }
        },
        addItem(item) {
            if (this.items && this.items.length > 0) {
                this.items = [...this.items, item]
            } else {
                this.items = [item]
            }
        },
        removeItem() {
            const index = this.items.findIndex(
                item => item.id == this.selectedItem.id
            )
            if (index >= 0) {
                this.items.splice(index, 1)
            }
        },
        async saveItem() {
            const supplier = this.suppliers.find(
                entry => entry.id === this.selectedSupplierId
            )
            if (!supplier) return

            try {
                this.formLoading = true
                if (this.createForm) {
                    const created = await API.createForeignTradeSupplier({
                        settingId: this.setting.id,
                        supplierId: supplier.id,
                        name: supplier.name,
                    })
                    this.addItem(created)
                } else {
                    const updated = await API.updateForeignTradeSupplier({
                        settingId: this.setting.id,
                        itemId: this.selectedItem.id,
                        supplierId: supplier.id,
                        name: supplier.name,
                    })
                    this.replaceItem({
                        id: this.selectedItem.id,
                        supplierId: supplier.id,
                        name: supplier.name,
                        ...updated,
                    })
                }
                this.closeEditItem()
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.formLoading = false
            }
        },
        async deleteItem() {
            try {
                this.loading = true
                await API.deleteInvoiceSettingItem({
                    settingId: this.setting.id,
                    itemId: this.selectedItem.id,
                    itemType: 'foreignTradeSupplier',
                })
                this.removeItem()
                this.closeDeleteItem()
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        closeDeleteItem() {
            this.selectedItem = {}
            this.openDeleteWarning = false
        },
        openDeleteItem(item) {
            this.selectedItem = _.cloneDeep(item)
            this.openDeleteWarning = true
        },
        closeEditItem() {
            this.createForm = true
            this.selectedItem = {}
            this.selectedSupplierId = null
            this.openItemForm = false
        },
        openEditItem(item) {
            this.createForm = false
            this.selectedItem = _.cloneDeep(item)
            this.selectedSupplierId = item.supplierId
            this.openItemForm = true
        },
        openCreateItem() {
            this.createForm = true
            this.selectedItem = {}
            this.selectedSupplierId = null
            this.openItemForm = true
        },
    },
}
</script>

<style>
.v-data-table {
    border: 1px solid #eeeeee;
}
thead {
    background: #eeeeee;
    position: sticky;
    z-index: 1;
}
.v-input .v-label {
    font-size: 12px;
}
.v-data-table-header th {
    background-color: #eeeeee !important;
}
.v-data-table__wrapper {
    max-height: 60vh;
    overflow-y: auto;
}
.v-data-table__row {
    height: 64px;
}
.v-data-table__wrapper thead tr {
    position: sticky;
    top: 0;
    z-index: 10;
}
</style>
